// app entrance
/**
 * Этот файл является точкой входа для веб-приложения.
 * Он отвечает за инициализацию приложения после загрузки DOM, обработку аутентификации пользователя,
 * загрузку данных из Firebase и настройку UI-компонентов.
 *
 * Основные функции файла:
 * - Отслеживание состояния аутентификации пользователя и управление доступом.
 * - Загрузка и обработка данных из Firebase, включая мэппинг сущностей со списками.
 * - Инициализация UI-компонентов, таких как маски ввода и элементы Tagify.
 * - Настройка слушателей событий для взаимодействия с пользователем (поиск, добавление записей, выход из аккаунта, экспорт данных).
 *
 * Этот файл экспортирует переменные и объекты, которые используются в других модулях приложения,
 * обеспечивая связь между различными частями кода.
 */

import Modal from 'bootstrap/js/dist/modal'
import { getAuth, onAuthStateChanged } from 'firebase/auth'
import '../scss/styles.scss'
import { stopAudio } from './audioPlayer.js'
import { map } from './constants.js'
import { exportToExcel } from './excel.js'
import { login, setPermissions } from './login.js'
import { createMaskDate, createMaskTime } from './mask.js'
import { searchData } from './search.js'
import { generatePreview, updateLecturesInAdvayta } from './server/advayta.js'
import { getUser, readFromFirebase } from './server/firebase.js'
import { secondsToHMS } from "./uiControl/audioForm"
import { clearBookFormHighlight } from "./uiControl/bookForm"
import { closeRightPanel, openRightPanel } from './uiControl/panels.js'
import { setupUserInfoPopover } from './uiControl/popoverControl.js'
import { collectFormData, resetAllFields } from './uiControl/rightPanel.js'
import { hideSpinnerOverlay, showSpinnerOverlay } from './uiControl/spinner.js'
import {
  columns,
  initTable,
  loadOriginalDataToTable,
  resetFilters,
  setOriginalData,
  table,
  tableAlertAndUpdate
} from './uiControl/tabulatorControl.js'
import { createSelectTagify } from './uiControl/tagifySelectControl.js'
import { showErrorToast, showOkToast } from './uiControl/toastControl.js'
import { deleteFile, upload } from './upload.js'
import { updateUserMarks } from './userMarksUtils.js'

// Объявление переменных
let currentEntity = getCurrentEntity() // Cущность, которую подгружаем из Firebase на текущий момент (lectures (лекции) или books (книги))
let entities // Хранит данные сущностей из Firebase
export let allData // Все данные, полученные из Firebase
export let user // Хранит информацию о текущем пользователе
export const tagifyElements = {} // Содержит экземпляры Tagify для различных полей
export const maskElements = {} // Содержит маски для полей даты и времени
export const inputElements = {
  key: document.getElementById('key'),
  title: document.getElementById('title'),
  comment: document.getElementById('comments'),
  description: document.getElementById('description'),
  youtube: document.getElementById('youtube-link'),
  advayta: document.getElementById('website-link'),
  seo_title: document.getElementById('seo-title'),
  seo_description: document.getElementById('seo-description'),
  text: document.getElementById('text'),
  text_link: document.getElementById('text-link'),
  preview: document.getElementById('preview'),
  paid: document.getElementById('paid'),
  topic: document.getElementById('topic'),
  bucket: document.getElementById('bucket'),
  path: document.getElementById('path'),
  storage_name: document.getElementById('storage_name'),
  supabase_file_id: document.getElementById('supabase-file-id'),
  lecture_link: document.getElementById('lecture-link')
}
// Метки пользователя
export const userMarksElement = {}

// Событие, которое срабатывает после загрузки DOM
document.addEventListener('DOMContentLoaded', () => {
  const auth = getAuth() // Получаем экземпляр Firebase Auth
  const loadingElements = initLoadingElements() // Инициализируем элементы загрузки

  // Отслеживаем изменение состояния аутентификации пользователя
  onAuthStateChanged(auth, async (userAuth) => {
    if (userAuth) {
      // Пользователь аутентифицирован
      console.log('Пользователь аутентифицирован:', userAuth)
      user = await getUser(userAuth) // Получаем информацию о пользователе из Firebase
      setupUserInfoPopover(user.name ? user.name : '')
      if (!entities) {
        // Если данные еще не загружены, инициализируем приложение
        await init(loadingElements)
      } else {
        updateUserMarks(user)
        // Данные уже загружены, скрываем индикатор загрузки и устанавливаем разрешения
        hideLoading(loadingElements)
        setPermissions(user.role)
      }
    } else {
      // Пользователь не аутентифицирован, показываем форму входа
      setupLoginForm(loadingElements)
      loadingElements.loginModal.show()
    }
  })

  setupEventListeners() // Настраиваем слушатели событий
})

/**
 * Инициализирует приложение: загружает данные и настраивает UI-компоненты.
 * @param {Object} loadingElements - Элементы, связанные с отображением загрузки.
 */
async function init(loadingElements) {
  try {
    const data = await fetchDataFromFirebase() // Получаем данные из Firebase
    entities = processEntitiesData(data) // Обрабатываем полученные данные
    setOriginalData(entities) // Устанавливаем исходные данные для таблицы
    initTable(function () {
      loadOriginalDataToTable() // Загружаем данные в таблицу
      initializeUIComponents(data) // Инициализируем UI-компоненты
      setPermissions(user.role) // Устанавливаем разрешения на основе роли пользователя
    })
  } catch (error) {
    console.error('Ошибка инициализации:', error)
    showError('Произошла ошибка во время инициализации.')
  } finally {
    hideLoading(loadingElements) // Скрываем индикатор загрузки в любом случае
  }
}

/**
 * Получает данные из Firebase.
 * @returns {Object} Данные, полученные из Firebase.
 */
async function fetchDataFromFirebase() {
  return allData = await readFromFirebase('/')
}

/**
 * Обрабатывает данные сущностей, связывая их с соответствующими списками.
 * @param {Object} data - Исходные данные из Firebase.
 * @returns {Array} Массив обработанных сущностей.
 */
function processEntitiesData(data) {
  let rawEntities = JSON.parse(JSON.stringify(data[currentEntity] || {}))
  const listsFromFB = extractListsFromData(data) // Извлекаем списки для мэппинга
  mapEntitiesWithLists(rawEntities, listsFromFB) // Связываем сущности со списками

  if (currentEntity === 'audios') {
    for (let i in rawEntities) {
      rawEntities[i].audioDuration = secondsToHMS(rawEntities[i].audioDuration)
    }
  }

  return Object.values(rawEntities).filter(e => currentEntity === 'books' ? e.lang === 'ru' : true) // Возвращаем массив сущностей
}

/**
 * Извлекает списки из данных для последующего мэппинга.
 * @param {Object} data - Исходные данные из Firebase.
 * @returns {Object} Объект, содержащий списки из Firebase.
 */
function extractListsFromData(data) {
  const lists = {}
  for (const key of Object.keys(map)) {
    const property = map[key]

    // Проверка наличия списка listName
    if (property.listName && !lists[property.listName]) {
      // Если listName соответствует меткам пользователя, используем динамический путь
      if (property.listName === map.marks.listName) {
        // Проверка наличия меток для пользователя
        lists[property.listName] = data.users?.[user.key]?.marks ? Object.values(data.users[user.key].marks) : []
      } else {
        // Обычная обработка для других списков
        lists[property.listName] = data[property.listName] ? Object.values(data[property.listName]) : []
      }
    }
  }
  return lists
}

/**
 * Связывает сущности с соответствующими элементами из списков на основе конфигурации в объекте 'map'.
 *
 * @param {Object} entities - Объект сущностей, где ключи - это идентификаторы сущностей, а значения - сами сущности.
 * @param {Object} lists - Объект списков, где ключи - это имена списков из 'listName' в 'map', а значения - массивы элементов списка.
 */
/**
 * Связывает сущности с соответствующими элементами из списков на основе конфигурации в объекте 'map',
 * а также добавляет к каждой сущности значения из меток ('marks'), если таковые существуют.
 *
 * @param {Object} entities - Объект сущностей, где ключи - это идентификаторы сущностей, а значения - сами сущности.
 * @param {Object} lists - Объект списков, где ключи - это имена списков из 'listName' в 'map', а значения - массивы элементов списка.
 */
function mapEntitiesWithLists(entities, lists) {
  // Проходим по каждой сущности в 'entities'
  for (const [key, entity] of Object.entries(entities)) {
    // Добавляем свойство 'key' в каждую сущность для хранения её уникального идентификатора
    entity.key = key

    // Проходим по каждой роли из объекта 'map'
    for (const property of Object.values(map)) {
      // Проверяем, определено ли 'listName' для текущей роли, если нет — пропускаем
      if (!property.listName) continue

      // Проверка на наличие поля marks (специальная обработка для меток)
      if (property.fieldName === map.marks.fieldName) {
        // Ищем метки, связанные с данной сущностью (по ключу)
        entity[property.fieldName] = lists[property.listName].filter(mark => mark.lectures && mark.lectures[key])
        continue // Переход к следующей роли, т.к. marks обрабатывается отдельно
      }

      // Проверяем, содержит ли текущая сущность поле, соответствующее 'fieldName' из текущей роли
      if (property.fieldName in entity) {
        let itemKey = entity[property.fieldName]
        if (!itemKey) continue
        // Проверяем, является ли поле одиночным или множественным
        if (property.isSingle) {
          entity[property.fieldName] = lists[property.listName].find((item) => item.key === (typeof itemKey === 'object' ? itemKey.key : itemKey))
        } else {
          if (['tags'].includes(property.listName)) {
            const tags = [...lists['tags']]
            let arr = []
            if (Array.isArray(itemKey)) {
              const keys = itemKey.filter(e => e).map(e => e.key)
              arr = tags.filter(e => keys.includes(e.key))
            } else {
              for (let key of Object.keys(itemKey)) {
                arr.push(tags.find((item) => item.key === (typeof key === 'object' ? key.key : key)))
              }
            }
            entity[property.listName] = arr
          } else {
            entity[property.listName] = Object.keys(itemKey).map((key) => lists[property.listName].find((item) => item.key === key))
          }
        }

      } else if (!property.isSingle) {
        // Инициализируем пустым массивом, если поле отсутствует и не одиночное (чтобы тэги были определены)
        entity[property.fieldName] = []
      }
    }
  }
}

/**
 * Инициализирует UI-компоненты, такие как Tagify и маски ввода.
 * @param {Object} data - Данные, используемые для инициализации компонентов.
 */
function initializeUIComponents(data) {
  // Инициализируем Tagify для каждого соответствующего поля
  for (const roleKey of Object.keys(map)) {
    const property = map[roleKey]
    if (property.listName) {
      // Определяем, какие данные использовать для заполнения тегов (marks или данные из property.listName)
      // Если listName совпадает с map.marks.listName, используем метки пользователя (user.marks)
      const listData = property.listName === map.marks.listName
        ? (user.marks ? Object.values(user.marks) : []) // Используем метки пользователя
        : (data[property.listName] ? Object.values(data[property.listName]) : [])
      tagifyElements[roleKey] = createSelectTagify(listData, property)
    }
  }

  // Инициализируем маски для полей даты и продолжительности
  const dateElement = document.getElementById('date')
  const durationElement = document.getElementById('duration')
  maskElements.date = createMaskDate(dateElement)
  maskElements.duration = createMaskTime(durationElement)
  durationElement.addEventListener("input", validateDuration)
  table.setColumns(columns[currentEntity])
}

function validateDuration() {
  const durationElement = document.getElementById("duration")
  const value = durationElement.value.trim()
  const pattern = /^[0-9]{2}:[0-5][0-9]:[0-5][0-9]$/
  const errorMessage = "Введите длительность в формате ЧЧ:ММ:СС."
  let errorElement = durationElement.nextElementSibling
  if (!errorElement || !errorElement.classList.contains("invalid-feedback")) {
    errorElement = document.createElement("div")
    errorElement.classList.add("invalid-feedback")
    durationElement.parentNode.appendChild(errorElement)
  }
  if (!pattern.test(value)) {
    durationElement.classList.add("is-invalid")
    durationElement.classList.remove("is-valid")
    errorElement.textContent = errorMessage
  } else {
    durationElement.classList.remove("is-invalid")
    durationElement.classList.add("is-valid")
    errorElement.textContent = ""
  }
}

/**
 * Настраивает слушатели событий для элементов интерфейса.
 */
function setupEventListeners() {
  // Слушатель для поиска
  const searchControl = document.getElementById('inputSearch')
  searchControl.focus()
  searchControl.addEventListener('keypress', searchData)
  searchControl.addEventListener('search', handleSearchClear)

  // Слушатель для кнопки добавления новой записи
  document.getElementById('addBtn').addEventListener('click', handleAddButtonClick)

  // Слушатель для кнопки экспорта в Excel
  document.getElementById('exportBtn').addEventListener('click', exportToExcel)

  // Показываем/скрываем список всех сущностей (dropdown)
  document.getElementById('entitiesDropdownButton').addEventListener('click', showEntities)

  // Изменение текущей сущности
  document.querySelectorAll('.entities-dropdown .dropdown-item').forEach(
    item => item.addEventListener('click', changeEntity)
  )

  // Загрузка файлов на сервер advayta.org с автоматической подстановкой ссылки в input
  document.querySelectorAll('.file-upload').forEach(btn => {
    btn.addEventListener('click', upload)
  })
  //Удаление файлов
  document.querySelectorAll('.file-delete').forEach(btn => btn.addEventListener('click', deleteFile))

  // Генерация превью
  const generatePreviewBtn = document.getElementById('generate-preview-btn')
  if (generatePreviewBtn) {
    generatePreviewBtn.addEventListener('click', handleGeneratePreview)
  }

  // Пересоздание страницы лекции (бэкенд 15.2: regenerate_content)
  const regeneratePageBtn = document.getElementById('regenerate-page-btn')
  if (regeneratePageBtn) {
    regeneratePageBtn.addEventListener('click', handleRegeneratePage)
  }
  ;['bucket', 'path'].forEach(id => {
    document.getElementById(id)?.addEventListener('input', updateRegenerateBtnState)
  })
  updateRegenerateBtnState()
}

/**
 * Обработчик события очистки поля поиска.
 */
function handleSearchClear() {
  tableAlertAndUpdate(loadOriginalDataToTable)
}

/**
 * Обработчик нажатия на кнопку добавления новой записи.
 */
function handleAddButtonClick() {
  stopAudio() // Останавливаем аудио при добавлении новой записи
  hideAudioPlayer() // Скрываем аудиоплеер
  table.deselectRow() // deselect all rows
  tagifyElements[map.marks.listName].setDisabled(true)
  clearBookFormHighlight()
  resetAllFields() // Сбрасываем все поля ввода
  openRightPanel() // Открываем правую панель для ввода новой записи
  updateRegenerateBtnState()
}

const SUPABASE_STORAGE_BASE = 'https://gxdisxwtiqeqjazydtiu.supabase.co/storage/v1/object/public'

// Реконструирует Supabase download URL из полей лекции (bucket/path хранятся в FRDB).
// supabase_download_link отдельно не персистится: при импорте он мапится в lecture-link,
// а при сохранении перезаписывается canonical-ссылкой (/ru/categories/...). Поэтому для
// регенерации собираем URL заново из bucket + path.
function buildSupabaseDownloadUrl() {
  const bucket = (inputElements.bucket?.value || '').trim()
  const path = (inputElements.path?.value || '').trim()
  if (!bucket || !path) return null
  return `${SUPABASE_STORAGE_BASE}/${bucket}/${path}`
}

// Кнопка «Пересоздать страницу» активна только когда лекция сохранена (есть key)
// и есть Supabase-источник (bucket + path). Иначе регенерировать не из чего.
export function updateRegenerateBtnState() {
  const btn = document.getElementById('regenerate-page-btn')
  if (!btn) return
  const key = (inputElements.key?.value || '').trim()
  const bucket = (inputElements.bucket?.value || '').trim()
  const path = (inputElements.path?.value || '').trim()
  btn.disabled = !key || !bucket || !path
}

// Bootstrap-модалка подтверждения вместо window.confirm(). Resolve true при «Пересоздать»,
// false при отмене / ESC / клике по backdrop (любое скрытие модалки).
function showConfirmRegenerateModal() {
  return new Promise((resolve) => {
    const modalEl = document.getElementById('confirmRegenerateModal')
    const confirmBtn = document.getElementById('confirm-regenerate-confirm-btn')
    if (!modalEl || !confirmBtn) {
      resolve(window.confirm('Пересоздать страницу лекции из источника?'))
      return
    }
    const modal = Modal.getOrCreateInstance(modalEl)
    let resolved = false
    const cleanup = () => {
      confirmBtn.removeEventListener('click', onConfirm)
      modalEl.removeEventListener('hidden.bs.modal', onHide)
    }
    const onConfirm = () => {
      resolved = true
      cleanup()
      modal.hide()
      resolve(true)
    }
    const onHide = () => {
      if (resolved) return
      cleanup()
      resolve(false)
    }
    confirmBtn.addEventListener('click', onConfirm)
    modalEl.addEventListener('hidden.bs.modal', onHide)
    modal.show()
  })
}

// Пересоздание страницы лекции: шлём обычное тело сохранения + regenerate_content и
// supabase_download_link. Бэкенд (15.2) скачивает текст и переписывает Content body
// (linked) либо создаёт новый Content (orphan); возвращает regenerated + canonical lecture_link.
async function handleRegeneratePage() {
  const supabaseUrl = buildSupabaseDownloadUrl()
  if (!supabaseUrl) {
    showErrorToast('Нет источника для пересоздания: заполните Bucket и Path лекции')
    return
  }
  const formData = collectFormData()
  if (!formData.key) {
    showErrorToast('Сначала сохраните лекцию, затем пересоздавайте страницу')
    return
  }
  const confirmed = await showConfirmRegenerateModal()
  if (!confirmed) return

  formData.regenerate_content = true
  formData.supabase_download_link = supabaseUrl

  showSpinnerOverlay()
  try {
    const response = await updateLecturesInAdvayta(formData)
    if (!response || response.error) {
      showErrorToast(response?.error || 'Не удалось пересоздать страницу')
      return
    }
    if (response.regenerated) {
      if (response.lecture_link) {
        inputElements.lecture_link.value = response.lecture_link
      }
      showOkToast('Страница лекции пересоздана. Не забудьте «Сохранить», чтобы обновить запись.')
    } else {
      showErrorToast('Бэкенд не подтвердил пересоздание (regenerated=false)')
    }
  } catch (error) {
    console.error('Ошибка пересоздания страницы:', error)
    showErrorToast('Ошибка при пересоздании страницы')
  } finally {
    hideSpinnerOverlay()
  }
}

async function handleGeneratePreview() {
  const generateBtn = document.getElementById('generate-preview-btn')
  const spinner = generateBtn.querySelector('.spinner-border')
  const previewInput = document.getElementById('preview')

  const topicField = document.getElementById('topic')
  const titleField = document.getElementById('title')
  const descriptionField = document.getElementById('description')

  let promptText = ''

  if (topicField && topicField.value.trim()) {
    promptText = topicField.value.trim()
  } else {
    const title = titleField && titleField.value.trim() ? titleField.value.trim() : ''
    const description = descriptionField && descriptionField.textContent.trim() ? descriptionField.textContent.trim() : ''

    if (title && description) {
      promptText = `${title}. ${description}`
    } else if (title) {
      promptText = title
    } else if (description) {
      promptText = description
    }
  }

  if (!promptText) {
    showErrorToast('Для генерации превью необходимо заполнить одно из полей: Тема, Название или Описание')
    return
  }

  console.log('Генерируем превью для текста:', promptText)

  try {
    spinner.classList.remove('d-none')
    generateBtn.querySelector('i').classList.add('d-none')
    generateBtn.disabled = true

    const result = await generatePreview(promptText)

    if (result && result?.url) {
      previewInput.value = result.url
      showOkToast('Превью успешно сгенерировано')
    } else {
      showErrorToast('Не удалось получить URL сгенерированного превью')
      throw new Error('Не удалось получить URL сгенерированного превью')
    }

  } catch (error) {
    showErrorToast('Не удалось сгенерировать превью')
  } finally {
    spinner.classList.add('d-none')
    generateBtn.querySelector('i').classList.remove('d-none')
    generateBtn.disabled = false
  }
}

/**
 * Инициализирует элементы, связанные с отображением загрузки.
 * @returns {Object} Элементы загрузки.
 */
function initLoadingElements() {
  const loadingGif = document.getElementById('loading') // Гифка загрузки
  const modalSignIn = document.getElementById('modalSignin') // Модальное окно входа
  const loginBtn = document.getElementById('loginBtn') // Кнопка входа
  const spinnerEl = loginBtn.querySelector('span.spinner-border') // Спиннер на кнопке входа
  const loginModal = Modal.getOrCreateInstance(modalSignIn) // Экземпляр модального окна Bootstrap

  return {
    loadingGif,
    loginModal,
    loginBtn,
    spinnerEl
  }
}

/**
 * Отображает индикатор загрузки и отключает элементы интерфейса.
 * @param {Object} loadingElements - Элементы загрузки.
 */
function showLoading(loadingElements) {
  loadingElements.spinnerEl.classList.remove('d-none') // Показываем спиннер
  loadingElements.loginBtn.setAttribute('disabled', '') // Отключаем кнопку входа
}

/**
 * Скрывает индикатор загрузки и включает элементы интерфейса.
 * @param {Object} loadingElements - Элементы загрузки.
 */
function hideLoading(loadingElements) {
  loadingElements.loginModal.hide() // Скрываем модальное окно входа
  loadingElements.spinnerEl.classList.add('d-none') // Скрываем спиннер
  loadingElements.loginBtn.removeAttribute('disabled') // Включаем кнопку входа
  loadingElements.loadingGif.classList.add('d-none') // Скрываем гифку загрузки
}

/**
 * Настраивает форму входа и ее слушатели.
 * @param {Object} loadingElements - Элементы загрузки.
 */
function setupLoginForm(loadingElements) {
  const signupForm = document.querySelector('#loginForm')
  signupForm.addEventListener('submit', async (e) => {
    e.preventDefault() // Предотвращаем перезагрузку страницы
    showLoading(loadingElements) // Показываем индикатор загрузки
    await login(e, signupForm) // Выполняем вход
  })

  signupForm.querySelector('#loginMail').value = '' // Очищаем поле email
  signupForm.querySelector('#loginPassword').value = '' // Очищаем поле пароля
}

/**
 * Отображает сообщение об ошибке пользователю.
 * @param {string} message - Текст сообщения об ошибке.
 */
function showError(message) {
  // Реализуйте логику отображения сообщения пользователю
  alert(message) // Пример реализации через alert
}

/**
 * Показываем/скрываем список всех сущностей (dropdown)
 * @param {Event} event - Event
 */
function showEntities(event) {
  const dropdownMenu = event.target.closest('.dropdown').querySelector('.dropdown-menu')
  dropdownMenu.classList.toggle('show')
}

/**
 * Изменяет текущую сущность и подгружает данные из Firebase
 * @param {Event} event - Event
 */
async function changeEntity(event) {
  closeRightPanel()
  clearBookFormHighlight()
  currentEntity = event.target.getAttribute('data-entity') // Текущая сущность: лекции (lectures) или книги (books)
  saveCurrentEntity(currentEntity)
  document.getElementById('entitiesDropdownButton').innerText = event.target.innerText // Меняем название у dropdown на выбранную сущность
  showEntities(event) // Скрываем dropdown menu
  const loadingElements = initLoadingElements() // Инициализируем элементы загрузки
  document.getElementById('loading').classList.remove('d-none') // Показываем индикатор загрузки
  entities = processEntitiesData(allData) // Обрабатываем полученные данные
  initTable(function () {
    setOriginalData(entities) // Устанавливаем исходные данные для таблицы
    loadOriginalDataToTable() // Загружаем данные в таблицу
    initializeUIComponents(allData) // Инициализируем UI-компоненты
  })
  resetFilters()
  if (tagifyElements['tags'] && tagifyElements['tags'].whitelist && allData['tags']) {
    tagifyElements['tags'].whitelist = Object.values(allData['tags'])
  }
  document.getElementById('loading').classList.add('d-none') // Скрываем индикатор загрузки
}

/**
 * Получаем текущую сущность из Local Storage (по умолчанию "Лекции")
 */
export function getCurrentEntity() {
  const entity = localStorage.getItem('entity') || 'lectures'
  const entityName = document.querySelector('.entities-dropdown .dropdown-item[data-entity="' + entity + '"]').textContent
  document.getElementById('entitiesDropdownButton').innerText = entityName
  return entity
}

/**
 * Сохраняем текущую сущность в Local Storage
 * @param {string} entity - lectures | books
 */
function saveCurrentEntity(entity) {
  return localStorage.setItem('entity', entity)
}

/**
 * Скрываем аудиоплеер
 */
export function hideAudioPlayer() {
  const audioPlayerContainer = document.getElementById('audio-player-container')
  audioPlayerContainer.classList.add('d-none')
}